import colors from '@honeybook/hbui/dist/styles/colors';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBSwitch from '@honeybook/hbui/dist/components/HBSwitch/HBSwitch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { PublicTemplateConfigurationProps } from '~/components/Templates/components/FlowTemplates/PublicTemplateConfigurationModal';
import { observer } from 'mobx-react';
import { useRootStore } from '~/stores/useRootStore';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { usePublishedTemplate } from '~/components/Templates/components/FlowTemplates/usePublishedTemplate';
import Copy16 from '@honeybook/icons/dist/icons/Copy16';
import { useGate } from 'statsig-react';
import { PUBLIC_TEMPLATE_UTM_SOURCE } from '~/components/ClientExperience/OneFlow/components/FlowView/PublishedTemplateViewHeader';
import {
    Link,
    LinkButton,
    LinkButtons,
    LinkContainer,
    ModalContent,
    OptionsContainer,
    TemplateLink
} from './PublishedTemplateModalContent';
import styled from 'styled-components';
import { mediumUp } from '@honeybook/hbui/dist/styles/breakpoints';
import Image16 from '@honeybook/icons/dist/icons/Image16';
import { flexColumn } from '@honeybook/hbui/dist/styles/mixins';
import { useGalleryTemplate } from '~/components/Templates/components/FlowTemplates/useGalleryTemplate';

const GalleryEligibilityModalContent = ({
    publicConfiguration
}: PublicTemplateConfigurationProps) => {
    const {
        templatesUiStore: { selectedTemplate }
    } = useRootStore();
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });
    const { isMediumUp } = useWindowSize();
    const { copyLink } = usePublishedTemplate({ publicConfiguration });
    const { generateThumbnails, toggleGalleryTemplate, isGalleryTemplate } =
        useGalleryTemplate({ publicConfiguration });
    const { value: isGalleryPublishingAllowed } = useGate(
        'template_thumbnails_generation'
    );

    if (!selectedTemplate) {
        return null;
    }

    const selectedTemplateId = selectedTemplate._id.toString();
    const fontSize = isMediumUp ? 'body14' : 'body16';
    const signupBaseUrl = window.location.origin;
    const signupUrl = `${signupBaseUrl}/signup?utm_source=${PUBLIC_TEMPLATE_UTM_SOURCE}&templateId=${selectedTemplate._id}&isGalleryTemplate=${isGalleryTemplate}`;

    return (
        <StyledModalContent>
            <PaddedOptionsContainer>
                {isGalleryPublishingAllowed && (
                    <ToggleContainer>
                        <ToggleTitle>
                            <HBSwitch
                                label={t('gallery_template_toggle')}
                                name={t('gallery_template_toggle')}
                                checked={isGalleryTemplate}
                                onChange={toggleGalleryTemplate}
                            ></HBSwitch>
                            <HBText type={fontSize}>
                                {t('gallery_template_toggle')}
                            </HBText>
                        </ToggleTitle>
                    </ToggleContainer>
                )}
            </PaddedOptionsContainer>
            <ColumnedLinkContainer>
                <HBText
                    color="textPrimary"
                    type="emphasis14"
                    children={t('gallery_tab.template_id_title')}
                />
                <LinkButtons>
                    <StyledColumnedLinkContainer>
                        <Link type={fontSize}>{selectedTemplateId}</Link>
                    </StyledColumnedLinkContainer>
                    <LinkButton
                        id="template_id"
                        onClick={() => {
                            copyLink(selectedTemplateId);
                        }}
                        size="xs"
                        customTheme={{
                            fontSize
                        }}
                        icon={<Copy16 color={colors.white} />}
                    >
                        {t('copy_cta')}
                    </LinkButton>
                </LinkButtons>
            </ColumnedLinkContainer>
            <ColumnedLinkContainer>
                <HBText
                    color="textPrimary"
                    type="emphasis14"
                    children={t('gallery_tab.preview_title')}
                />
                <LinkButtons>
                    <StyledColumnedLinkContainer>
                        <Link type={fontSize}>
                            {publicConfiguration?.public_url}
                        </Link>
                    </StyledColumnedLinkContainer>
                    <LinkButton
                        id="preview_template_url"
                        onClick={() => {
                            copyLink(publicConfiguration?.public_url);
                        }}
                        size="xs"
                        customTheme={{
                            fontSize
                        }}
                        icon={<Copy16 color={colors.white} />}
                    >
                        {t('copy_cta')}
                    </LinkButton>
                </LinkButtons>
            </ColumnedLinkContainer>
            <ColumnedLinkContainer>
                <HBText
                    color="textPrimary"
                    type="emphasis14"
                    children={t('gallery_tab.get_template_title')}
                />
                <LinkButtons>
                    <StyledColumnedLinkContainer>
                        <Link type={fontSize}>{signupUrl}</Link>
                    </StyledColumnedLinkContainer>
                    <LinkButton
                        id="signup_url"
                        onClick={() => {
                            copyLink(signupUrl);
                        }}
                        size="xs"
                        customTheme={{
                            fontSize
                        }}
                        icon={<Copy16 color={colors.white} />}
                    >
                        {t('copy_cta')}
                    </LinkButton>
                </LinkButtons>
            </ColumnedLinkContainer>{' '}
            <GenerateThumbnailsButtonContainer>
                <HBText
                    color="textPrimary"
                    type="emphasis14"
                    children={t('gallery_tab.generate_title')}
                />

                <LinkButtons>
                    <LinkButton
                        id="generate_thumbnails"
                        onClick={generateThumbnails}
                        size="xs"
                        customTheme={{
                            fontSize
                        }}
                        icon={<Image16 color={colors.white} />}
                    >
                        {t('gallery_tab.generate_cta')}
                    </LinkButton>
                </LinkButtons>
            </GenerateThumbnailsButtonContainer>
        </StyledModalContent>
    );
};

export default observer(GalleryEligibilityModalContent);

const GenerateThumbnailsButtonContainer = styled(LinkContainer)`
    align-items: center;
    gap: 16px;
`;

const StyledModalContent = styled(ModalContent)`
    width: 464px;
`;

const StyledColumnedLinkContainer = styled(TemplateLink)`
    ${mediumUp} {
        width: 254px;
    }
`;

const ColumnedLinkContainer = styled(LinkContainer)`
    width: 100%;
    ${flexColumn}
    gap: 7px;
`;

const PaddedOptionsContainer = styled(OptionsContainer)`
    padding: 32px 0 0 0;
`;

const ToggleContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ToggleTitle = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;
